
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Book, Search, ChevronRight, Copy, Check, FileText, BookOpen, VideoIcon, Users, Home, Settings } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const DocumentationPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [copied, setCopied] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('getting-started');
  const [activeSection, setActiveSection] = useState('introduction');

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleScroll = () => {
    const sections = document.querySelectorAll('.doc-section');
    const scrollPosition = window.scrollY + 100;

    sections.forEach((section) => {
      const element = section as HTMLElement;
      const id = element.id;
      const offset = element.offsetTop;
      const height = element.offsetHeight;

      if (scrollPosition >= offset && scrollPosition < offset + height) {
        setActiveSection(id);
      }
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 80,
        behavior: 'smooth'
      });
    }
  };

  const categories = [
    {
      id: 'getting-started',
      label: 'Commencer',
      icon: <Home className="w-5 h-5 mr-2" />,
      sections: [
        { id: 'introduction', label: 'Introduction' },
        { id: 'installation', label: 'Installation' },
        { id: 'premiers-pas', label: 'Premiers pas' }
      ]
    },
    {
      id: 'features',
      label: 'Fonctionnalités',
      icon: <VideoIcon className="w-5 h-5 mr-2" />,
      sections: [
        { id: 'videos', label: 'Gestion des vidéos' },
        { id: 'playlists', label: 'Collections de vidéos' },
        { id: 'search', label: 'Recherche avancée' }
      ]
    },
    {
      id: 'creators',
      label: 'Créateurs',
      icon: <Users className="w-5 h-5 mr-2" />,
      sections: [
        { id: 'profile', label: 'Profil créateur' },
        { id: 'analytics', label: 'Analytiques' },
        { id: 'monetization', label: 'Monétisation' }
      ]
    },
    {
      id: 'api',
      label: 'API',
      icon: <FileText className="w-5 h-5 mr-2" />,
      sections: [
        { id: 'authentication', label: 'Authentification' },
        { id: 'endpoints', label: 'Points de terminaison' },
        { id: 'rate-limits', label: 'Limites de taux' }
      ]
    },
    {
      id: 'advanced',
      label: 'Avancé',
      icon: <Settings className="w-5 h-5 mr-2" />,
      sections: [
        { id: 'customize', label: 'Personnalisation' },
        { id: 'integrations', label: 'Intégrations' },
        { id: 'security', label: 'Sécurité' }
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    }
  };

  return (
    <Layout>
      <div className="flex flex-col overflow-x-hidden w-full">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-youtube-red to-red-600 text-white py-10 md:py-20 px-4">
          <div className="max-w-5xl mx-auto text-center">
            <motion.h1 
              className="text-3xl md:text-5xl font-bold mb-4 md:mb-6"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Documentation PersonalTube
            </motion.h1>
            <motion.p 
              className="text-lg md:text-2xl max-w-3xl mx-auto mb-6 md:mb-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              Tout ce que vous devez savoir pour maîtriser PersonalTube
            </motion.p>
            
            <motion.div 
              className="flex flex-col md:flex-row gap-4 justify-center max-w-3xl mx-auto relative"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <Input
                type="text"
                placeholder="Rechercher dans la documentation..."
                className="flex-1 h-12 md:h-14 text-black dark:text-white rounded-full px-6 shadow-lg focus:ring-2 focus:ring-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button variant="youtube" size="lg" className="h-12 md:h-14 px-8 rounded-full">
                <Search className="mr-2" />
                Rechercher
              </Button>
            </motion.div>
          </div>
        </section>
        
        {/* Documentation Content */}
        <section className="py-8 md:py-16">
          <div className="max-w-7xl mx-auto px-4 flex flex-col lg:flex-row gap-8">
            {/* Sidebar Navigation - Fixed on mobile, dropdown on desktop */}
            <div className="lg:w-1/4 w-full">
              <motion.aside 
                className="lg:sticky lg:top-24 self-start"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <BookOpen className="w-5 h-5 mr-2 text-youtube-red" />
                    Documentation
                  </h3>
                  
                  <Tabs 
                    value={selectedCategory} 
                    onValueChange={setSelectedCategory}
                    orientation="vertical" 
                    className="w-full"
                  >
                    <TabsList className="flex flex-col w-full bg-transparent space-y-1 h-auto">
                      {categories.map((category) => (
                        <TabsTrigger 
                          key={category.id} 
                          value={category.id}
                          className="justify-start py-2 text-left w-full"
                        >
                          <div className="flex items-center">
                            {category.icon}
                            {category.label}
                          </div>
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    
                    {categories.map((category) => (
                      <TabsContent 
                        key={category.id} 
                        value={category.id}
                        className="mt-4 border-t pt-4"
                      >
                        <ul className="space-y-2">
                          {category.sections.map((section) => (
                            <motion.li 
                              key={section.id}
                              whileHover={{ x: 3 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <button
                                onClick={() => scrollToSection(section.id)}
                                className={`flex items-center text-sm py-1 pl-4 w-full text-left hover:text-youtube-red transition-colors ${
                                  activeSection === section.id 
                                    ? 'text-youtube-red font-medium border-l-2 border-youtube-red' 
                                    : 'text-gray-600 dark:text-gray-400'
                                }`}
                              >
                                {section.label}
                              </button>
                            </motion.li>
                          ))}
                        </ul>
                      </TabsContent>
                    ))}
                  </Tabs>
                </div>
              </motion.aside>
            </div>
            
            {/* Main Content */}
            <div className="lg:w-3/4 w-full">
              <Card className="mb-8 overflow-hidden">
                <CardContent className="p-4 md:p-8">
                  <div id="introduction" className="doc-section mb-8 md:mb-16">
                    <motion.h2 
                      className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 flex items-center text-youtube-red"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Book className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3 text-youtube-red" />
                      Introduction à PersonalTube
                    </motion.h2>
                    
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2, duration: 0.5 }}
                    >
                      <p className="text-base md:text-lg mb-4">
                        PersonalTube est une plateforme innovante qui vous permet de gérer et organiser vos vidéos préférées en un seul endroit. Que vous soyez un simple utilisateur ou un créateur de contenu, PersonalTube offre une expérience personnalisée pour profiter au maximum de vos contenus.
                      </p>
                      
                      <p className="text-base md:text-lg mb-4">
                        Cette documentation complète vous guidera à travers toutes les fonctionnalités et vous aidera à tirer le meilleur parti de PersonalTube. Que vous soyez débutant ou utilisateur avancé, vous trouverez ici toutes les informations nécessaires.
                      </p>
                      
                      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 md:p-6 mt-6 md:mt-8">
                        <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Caractéristiques principales:</h3>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <ChevronRight className="w-5 h-5 text-youtube-red mr-2 mt-0.5 flex-shrink-0" />
                            <span>Organisez vos vidéos préférées en collections personnalisées</span>
                          </li>
                          <li className="flex items-start">
                            <ChevronRight className="w-5 h-5 text-youtube-red mr-2 mt-0.5 flex-shrink-0" />
                            <span>Suivez vos créateurs de contenu préférés</span>
                          </li>
                          <li className="flex items-start">
                            <ChevronRight className="w-5 h-5 text-youtube-red mr-2 mt-0.5 flex-shrink-0" />
                            <span>Découvrez du nouveau contenu adapté à vos intérêts</span>
                          </li>
                          <li className="flex items-start">
                            <ChevronRight className="w-5 h-5 text-youtube-red mr-2 mt-0.5 flex-shrink-0" />
                            <span>Interface intuitive et expérience utilisateur fluide</span>
                          </li>
                          <li className="flex items-start">
                            <ChevronRight className="w-5 h-5 text-youtube-red mr-2 mt-0.5 flex-shrink-0" />
                            <span>Synchronisation entre tous vos appareils</span>
                          </li>
                        </ul>
                      </div>
                    </motion.div>
                  </div>
                  
                  <div id="installation" className="doc-section mb-8 md:mb-16">
                    <motion.h2 
                      className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 text-youtube-red"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      Installation
                    </motion.h2>
                    
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2, duration: 0.5 }}
                    >
                      <p className="text-base md:text-lg mb-4">
                        PersonalTube est une application web accessible directement depuis votre navigateur. Aucune installation n'est nécessaire pour commencer à l'utiliser.
                      </p>
                      
                      <h3 className="text-lg md:text-xl font-semibold mt-6 md:mt-8 mb-3 md:mb-4">Configuration requise</h3>
                      
                      <p className="mb-4">
                        Pour une expérience optimale, nous recommandons d'utiliser:
                      </p>
                      
                      <ul className="list-disc pl-6 mb-6 md:mb-8 space-y-2">
                        <li>Un navigateur web moderne (Chrome, Firefox, Safari, Edge)</li>
                        <li>Une connexion Internet stable</li>
                        <li>JavaScript activé dans votre navigateur</li>
                      </ul>
                      
                      <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-youtube-red p-4 rounded-r-lg mb-6 md:mb-8">
                        <p className="text-red-800 dark:text-red-200">
                          <strong>Note:</strong> PersonalTube est également disponible sous forme d'application pour les appareils mobiles. Vous pouvez télécharger l'application depuis l'App Store ou Google Play.
                        </p>
                      </div>
                      
                      <h3 className="text-lg md:text-xl font-semibold mt-6 md:mt-8 mb-3 md:mb-4">Premiers pas avec l'API</h3>
                      
                      <p className="mb-4">
                        Si vous êtes développeur et souhaitez intégrer PersonalTube à votre application, voici comment commencer avec notre API:
                      </p>
                      
                      <div className="relative bg-gray-800 text-gray-200 rounded-lg p-4 mb-4 overflow-x-auto">
                        <button 
                          className="absolute top-4 right-4 text-gray-400 hover:text-white"
                          onClick={() => handleCopy('npm install personaltube-sdk --save')}
                        >
                          {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                        </button>
                        <pre className="whitespace-pre-wrap break-words"><code>npm install personaltube-sdk --save</code></pre>
                      </div>
                      
                      <div className="relative bg-gray-800 text-gray-200 rounded-lg p-4 overflow-x-auto">
                        <button 
                          className="absolute top-4 right-4 text-gray-400 hover:text-white"
                          onClick={() => handleCopy('import { PersonalTube } from \'personaltube-sdk\';\n\nconst client = new PersonalTube({\n  apiKey: \'VOTRE_CLE_API\',\n});\n\n// Exemple de requête\nconst videos = await client.getVideos({ limit: 10 });')}
                        >
                          {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                        </button>
                        <pre className="whitespace-pre-wrap break-words text-sm"><code>{`import { PersonalTube } from 'personaltube-sdk';

const client = new PersonalTube({
  apiKey: 'VOTRE_CLE_API',
});

// Exemple de requête
const videos = await client.getVideos({ limit: 10 });`}</code></pre>
                      </div>
                    </motion.div>
                  </div>
                  
                  <div id="premiers-pas" className="doc-section mb-8 md:mb-16">
                    <motion.h2 
                      className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 text-youtube-red"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      Premiers pas
                    </motion.h2>
                    
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2, duration: 0.5 }}
                    >
                      <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Création d'un compte</h3>
                      
                      <p className="mb-4">
                        Pour commencer à utiliser PersonalTube, vous devez créer un compte en utilisant votre adresse e-mail ou en vous connectant avec Google ou Facebook.
                      </p>
                      
                      <ol className="list-decimal pl-6 mb-6 md:mb-8 space-y-3 md:space-y-4">
                        <li>
                          <strong>Accédez à PersonalTube:</strong> Rendez-vous sur la page d'accueil de PersonalTube et cliquez sur le bouton "Commencer maintenant".
                        </li>
                        <li>
                          <strong>Choisissez votre méthode d'inscription:</strong> Vous pouvez vous inscrire avec votre adresse e-mail ou utiliser vos comptes Google ou Facebook.
                        </li>
                        <li>
                          <strong>Remplissez le formulaire:</strong> Si vous choisissez l'inscription par e-mail, remplissez le formulaire avec votre nom, votre adresse e-mail et un mot de passe sécurisé.
                        </li>
                        <li>
                          <strong>Vérifiez votre e-mail:</strong> Un e-mail de vérification sera envoyé à l'adresse fournie. Cliquez sur le lien de vérification pour activer votre compte.
                        </li>
                        <li>
                          <strong>Personnalisez votre profil:</strong> Une fois votre compte créé, vous pouvez personnaliser votre profil en ajoutant une photo de profil et en configurant vos préférences.
                        </li>
                      </ol>
                      
                      <h3 className="text-lg md:text-xl font-semibold mt-6 md:mt-8 mb-3 md:mb-4">Navigation dans l'interface</h3>
                      
                      <p className="mb-4">
                        L'interface de PersonalTube est conçue pour être intuitive et facile à utiliser. Voici les principales sections:
                      </p>
                      
                      <Accordion type="multiple" className="w-full mb-6 md:mb-8">
                        <AccordionItem value="accueil">
                          <AccordionTrigger className="text-base md:text-lg font-medium">
                            Page d'accueil
                          </AccordionTrigger>
                          <AccordionContent>
                            <p className="mb-2">
                              La page d'accueil présente les vidéos recommandées en fonction de vos intérêts et de votre historique de visionnage. C'est le point de départ idéal pour découvrir de nouveaux contenus.
                            </p>
                            <ul className="list-disc pl-6 space-y-1">
                              <li>Vidéos tendance</li>
                              <li>Recommandations personnalisées</li>
                              <li>Contenus récents de vos créateurs préférés</li>
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                        
                        <AccordionItem value="flux">
                          <AccordionTrigger className="text-base md:text-lg font-medium">
                            Flux de vidéos
                          </AccordionTrigger>
                          <AccordionContent>
                            <p className="mb-2">
                              Le flux affiche les vidéos des créateurs que vous suivez, organisées chronologiquement.
                            </p>
                            <ul className="list-disc pl-6 space-y-1">
                              <li>Filtres par catégorie ou par créateur</li>
                              <li>Notifications pour les nouvelles vidéos</li>
                              <li>Options de tri personnalisées</li>
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                        
                        <AccordionItem value="createurs">
                          <AccordionTrigger className="text-base md:text-lg font-medium">
                            Découverte de créateurs
                          </AccordionTrigger>
                          <AccordionContent>
                            <p className="mb-2">
                              Cette section vous permet de découvrir de nouveaux créateurs de contenu en fonction de vos intérêts.
                            </p>
                            <ul className="list-disc pl-6 space-y-1">
                              <li>Créateurs tendance</li>
                              <li>Suggestions personnalisées</li>
                              <li>Catégories de contenu</li>
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                        
                        <AccordionItem value="collections">
                          <AccordionTrigger className="text-base md:text-lg font-medium">
                            Gestion des collections
                          </AccordionTrigger>
                          <AccordionContent>
                            <p className="mb-2">
                              Créez et gérez vos collections personnalisées pour organiser vos vidéos par thème, sujet ou tout autre critère de votre choix.
                            </p>
                            <ul className="list-disc pl-6 space-y-1">
                              <li>Création de nouvelles collections</li>
                              <li>Organisation et tri des vidéos</li>
                              <li>Options de partage et de confidentialité</li>
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                      
                      <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-youtube-red p-4 rounded-r-lg">
                        <p className="text-red-800 dark:text-red-200">
                          <strong>Conseil:</strong> Utilisez la fonction de recherche en haut de la page pour trouver rapidement des vidéos ou des créateurs spécifiques. La recherche avancée vous permet de filtrer les résultats par date, popularité et catégorie.
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="flex justify-between items-center mt-8 md:mt-12 px-4">
                <Button variant="outline" className="flex items-center gap-2 text-youtube-red hover:bg-red-50 hover:border-youtube-red">
                  <ChevronRight className="w-4 h-4 rotate-180" />
                  Page précédente
                </Button>
                
                <Button variant="outline" className="flex items-center gap-2 text-youtube-red hover:bg-red-50 hover:border-youtube-red">
                  Page suivante
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
      
      <footer className="bg-youtube-red text-white py-6 md:py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p>© {new Date().getFullYear()} PersonalTube. Tous droits réservés.</p>
        </div>
      </footer>
    </Layout>
  );
};

export default DocumentationPage;
